import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { NumberInput } from '@/components/molecules/NumberInput';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { MANAGE_PROFILES_LABEL, MANAGE_PROFILES_VALUE } from './support/manage-profiles-option';
import { DurationMinutesInput } from '@/components/molecules/DurationInput';
import type { LaborRateProfile, ServiceLaborLine } from '@/types/pricebook';
import {
  laborLineAmount,
  laborLinesMissingRateMinutes,
  fixedRateAllowedFor,
  laborProfilesByKind,
  laborLinesTotal,
  marginPercentFor,
  marginShareLaborTotal,
  rowTakesMarginShare,
  selectableLaborProfiles,
  splitLaborLines,
} from '@/lib/sales/service-labor';

/**
 * The people, the time, and the rate — the one editor, and the ONE rate.
 *
 * THERE IS NO SEPARATE LABOR COST RATE. A labor rate is what labor per unit of
 * time is worth, and it is the same number whichever way the service is priced.
 * The two modes differ only in what that number then DOES:
 *
 *   BILLING  a DYNAMIC service is priced FROM it — `(hours x labor rate)` is
 *            the labor term of the formula, so it moves what the customer pays.
 *   COST     a FLAT service's price is the number the operator typed, so the
 *            same `hours x rate` lands in the Labor figure on the cost panel
 *            and moves gross margin instead. Nothing the customer pays moves.
 *
 * `basis` therefore selects WORDING and where the total is read, never a second
 * rate to look up. An earlier pass modelled a per-profile cost rate alongside
 * the billing tiers; the owner's answer was that no such number exists, so the
 * tiers are the whole answer for a profile in both modes.
 *
 * Shared rather than copied because the two drifted the moment they were
 * separate: the flat side grew a bare "hours" spinner while the dynamic side
 * already had a duration control reading "1h 30m", and one of them was wrong.
 *
 * DURATION IS STORED IN MINUTES, on both sides, because `ServiceLaborLine` and
 * the dynamic pricing engine already are. Hours are a presentation of minutes
 * and a derivation (`minutes / 60`); nothing stores a third unit.
 *
 * See SPEC-line-item-model, "Flat vs Dynamic".
 */

export type LaborRateBasis = 'BILLING' | 'COST';

const money = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);

export const DEFAULT_LABOR_LINE: ServiceLaborLine = {
  label: '',
  durationMinutes: 60,
  rateSource: 'FIXED',
  fixedRate: 0,
};

/**
 * The labor arithmetic moved to `lib/sales/service-labor.ts`, where `serviceCost`
 * can ask it without pulling this component into the costing path. Re-exported
 * so every existing import keeps working and there is still only one answer.
 */
export {
  laborLineAmount,
  laborLinesHours,
  laborLinesMinutes,
  laborLinesMissingRateMinutes,
  laborLinesTotal,
} from '@/lib/sales/service-labor';

/**
 * Why this row is worth nothing, when it plainly has time in it.
 *
 * `null` when the amount is genuinely non-zero, or when there is no duration
 * yet — an empty row explaining itself is noise.
 */
export function zeroAmountReason(
  line: ServiceLaborLine,
  profiles: LaborRateProfile[],
): string | null {
  const minutes = Math.max(0, line.durationMinutes || 0);
  if (minutes <= 0) return null;
  if (laborLineAmount(line, profiles) > 0) return null;

  if (line.rateSource === 'FIXED') {
    return 'Set an hourly rate — duration alone does not produce an amount.';
  }
  const profile = profiles.find((candidate) => candidate.id === line.laborRateProfileId);
  if (!profile) return 'Choose a labor-rate profile to price this time.';
  return `"${profile.name}" has no rate tiers, so this time is worth nothing.`;
}

const COPY: Record<LaborRateBasis, { blurb: string; rateLabel: string; totalLabel: string }> = {
  BILLING: {
    blurb: 'Estimate the people, hours, and labor rate needed.',
    rateLabel: 'Hourly rate',
    totalLabel: 'Estimated labor',
  },
  COST: {
    // Same rate, different consequence: a flat price is already set, so this
    // time lands in cost and margin rather than in what the customer pays.
    blurb:
      'The people and hours this service takes, at the same labor rates. ' +
      'The price is already set, so this moves cost and gross margin only.',
    rateLabel: 'Hourly rate',
    totalLabel: 'Labor cost',
  },
};

export function LaborLinesEditor({
  lines,
  onChange,
  basis,
  profiles,
  onManageProfiles,
  headerRight,
  contribution,
}: {
  lines: ServiceLaborLine[];
  onChange: (lines: ServiceLaborLine[]) => void;
  basis: LaborRateBasis;
  profiles: LaborRateProfile[];
  onManageProfiles?: () => void;
  /** Extra control rendered beside "Add another labor rate". */
  headerRight?: React.ReactNode;
  /**
   * What the service earns over its materials and equipment — the money a
   * margin-share row takes its percentage from.
   *
   * Optional because most editors have no such row and would have nothing to do
   * with it. Without it a margin-share row still states its PERCENTAGE, which is
   * the part the operator authored; it simply cannot price it, and says so
   * rather than printing a confident $0.00.
   */
  contribution?: number;
}) {
  const copy = COPY[basis];
  const updateLine = (index: number, next: Partial<ServiceLaborLine>) =>
    onChange(lines.map((line, i) => (i === index ? { ...line, ...next } : line)));

  /**
   * ONE KIND OF LABOR PER SERVICE.
   *
   * Hours and a share of the margin are two different answers to "what is this
   * crew paid", and a service that carries both is a service nobody can read:
   * the hourly rows say the work costs what it takes, the share says it costs
   * what the job earns, and the total is neither. The rule is enforced in the
   * two directions it can be broken — adding a row beside a share, and choosing
   * a share beside hourly rows — and `splitLaborLines` is the same predicate
   * costing uses, so the editor cannot permit a shape the costing mis-prices.
   *
   * The rule bars MIXING, never REPEATING: two crews each taking a share is one
   * answer given twice, and the shares add up against the same contribution
   * (see `marginShareLaborTotal`). As many rows as there are crews to pay.
   *
   * A margin share is also FLAT-ONLY: a dynamic service's price is calculated
   * FROM its labor, so paying that labor a share of the price is circular. The
   * profiles are simply not offered on the billing side.
   */
  const { hourly, marginShare } = splitLaborLines(lines, profiles);
  const marginShareAllowed = basis === 'COST';
  const hasMarginShare = marginShare.length > 0;

  /**
   * Are there HOURLY rows other than this one?
   *
   * Asked per row rather than globally because the row being edited is itself
   * hourly until the moment a share is chosen; counting it would make the
   * option permanently unavailable on the very first row.
   */

  const marginShareAmount = hasMarginShare
    ? marginShareLaborTotal(marginShare, profiles, contribution ?? 0)
    : 0;
  const total = laborLinesTotal(hourly, profiles) + marginShareAmount;
  const missingRateMinutes = basis === 'COST' ? laborLinesMissingRateMinutes(lines, profiles) : 0;

  // Sized against THIS editor, not the viewport. It sits in the estimate line
  // editor beside a cost rail and behind the app sidebar, so a 768px window
  // (`md:`) says nothing about the ~300px it actually has. A container-type
  // element cannot query itself, only its descendants can, so the query lives
  // here and the rows below read it.
  return (
    <div className="@container space-y-2">
      <div className="flex flex-col gap-2 @lg:flex-row @lg:items-center @lg:justify-between">
        <div className="min-w-0">
          <span className="text-sm font-medium">Labor</span>
          <p className="text-xs text-muted-foreground">{copy.blurb}</p>
        </div>
        {/* Wraps rather than refusing to shrink, so a second control in
            `headerRight` stacks under the first instead of pushing past the
            editor's edge. The buttons keep their width through their own
            nowrap min-content; the blurb is what gives way. */}
        <div className="flex flex-wrap items-center gap-2">
          {headerRight}
          <Button
            type="button"
            size="sm"
            variant="outline"
            // A new row joins the kind already in use — see the exclusivity
            // note above. A blank hourly row dropped into a service that pays
            // shares would be a mix the operator has to undo before they can
            // pick the profile they came for.
            onClick={() =>
              onChange([
                ...lines,
                hasMarginShare
                  ? {
                      ...DEFAULT_LABOR_LINE,
                      rateSource: 'PROFILE' as const,
                      durationMinutes: 0,
                      fixedRate: 0,
                    }
                  : { ...DEFAULT_LABOR_LINE },
              ])
            }
          >
            <Plus size={14} className="mr-1" />{' '}
            {hasMarginShare ? 'Add another margin share' : 'Add another labor rate'}
          </Button>
        </div>
      </div>

      {hasMarginShare ? (
        <p className="rounded-md border bg-muted/40 p-2.5 text-xs text-muted-foreground">
          This labor takes a share of what the service earns over its materials and equipment. Add
          as many shares as there are crews to pay — they all come out of the same margin, so there
          are no hours to enter beside them.
        </p>
      ) : null}

      {lines.map((line, index) => (
        <div
          key={line.id ?? index}
          // `minmax(0, …)` because a bare `1fr` floors at the column's content
          // width, so the inputs and the amount refused to shrink and the row
          // spilled out of its border. Below `@md` the five columns become two
          // (duration / rate type, then rate / amount) rather than a stack.
          className="grid grid-cols-2 gap-2 rounded-md border bg-background p-3 @md:grid-cols-[minmax(0,.65fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,.8fr)_auto]"
        >
          <label className="min-w-0 space-y-1">
            <span className="text-xs text-muted-foreground">Duration</span>
            {/* MINUTES, the stored unit, typed and rendered as "1h 30m". Costing
                divides by 60, so a half hour costs half an hour — a plain number
                field here silently made 1.5 hours read as 1 or 2. A bare number
                is HOURS, the same as every other labor field.

                A margin-share row is not sold by the hour, so it has no duration
                to type. Leaving the control there and ignoring what was typed
                would be worse than removing it: the operator would set a time
                and watch it change nothing. */}
            {rowTakesMarginShare(lines, index, profiles) ? (
              <span className="block h-10 pt-2 text-sm text-muted-foreground">Not hourly</span>
            ) : (
              <DurationMinutesInput
                value={line.durationMinutes}
                onChange={(durationMinutes) => updateLine(index, { durationMinutes })}
              />
            )}
          </label>
          <label className="min-w-0 space-y-1">
            <span className="text-xs text-muted-foreground">Rate type</span>
            <Select
              value={line.rateSource}
              onValueChange={(rateSource) =>
                updateLine(index, { rateSource: rateSource as ServiceLaborLine['rateSource'] })
              }
            >
              <SelectTrigger aria-label="Rate type" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {/* Not offered once this service pays shares of the margin:
                    a fixed hourly rate beside them would mix the two kinds. */}
                {fixedRateAllowedFor(lines, index, profiles) ? (
                  <SelectItem value="FIXED">Fixed rate</SelectItem>
                ) : null}
                <SelectItem value="PROFILE">Rate profile</SelectItem>
              </SelectContent>
            </Select>
          </label>
          {line.rateSource === 'FIXED' ? (
            <label className="min-w-0 space-y-1">
              <span className="text-xs text-muted-foreground">{copy.rateLabel}</span>
              <NumberInput
                aria-label={copy.rateLabel}
                min="0"
                step="0.01"
                value={line.fixedRate ?? 0}
                emptyValue={0}
                onChange={(next) => updateLine(index, { fixedRate: Math.max(0, next ?? 0) })}
              />
            </label>
          ) : (
            <label className="min-w-0 space-y-1">
              <span className="text-xs text-muted-foreground">Labor-rate profile</span>
              <Select
                value={line.laborRateProfileId ?? ''}
                onValueChange={(laborRateProfileId) => {
                  // Never a storable value — it opens the editor instead.
                  if (laborRateProfileId === MANAGE_PROFILES_VALUE) {
                    onManageProfiles?.();
                    return;
                  }
                  const chosen = profiles.find((profile) => profile.id === laborRateProfileId);
                  updateLine(index, {
                    laborRateProfileId,
                    // A margin share prices no time, so a duration left behind
                    // would be time the schedule plans for and nobody is paid
                    // for. Cleared where the row stops being hourly.
                    ...(chosen?.kind === 'MARGIN_PERCENT'
                      ? { durationMinutes: 0, fixedRate: 0 }
                      : {}),
                  });
                }}
              >
                <SelectTrigger aria-label="Labor-rate profile" className="w-full">
                  <SelectValue placeholder="Choose profile" />
                </SelectTrigger>
                <SelectContent>
                  {(() => {
                    // Margin shares first, then a line, then the hourly
                    // profiles: the two kinds never mix on one service.
                    const { shares, hourly } = laborProfilesByKind(
                      selectableLaborProfiles(
                        lines,
                        index,
                        profiles.filter((profile) => profile.isActive),
                        { marginShareAllowed },
                      ),
                    );
                    const item = (profile: LaborRateProfile) => (
                      <SelectItem key={profile.id} value={profile.id}>
                        {profile.name}
                        {profile.kind === 'MARGIN_PERCENT'
                          ? ` — ${profile.marginPercent ?? 0}% of margin`
                          : ''}
                      </SelectItem>
                    );
                    return (
                      <>
                        {shares.map(item)}
                        {shares.length > 0 && hourly.length > 0 ? <SelectSeparator /> : null}
                        {hourly.map(item)}
                      </>
                    );
                  })()}
                  {onManageProfiles ? (
                    <>
                      <SelectSeparator />
                      <SelectItem value={MANAGE_PROFILES_VALUE}>{MANAGE_PROFILES_LABEL}</SelectItem>
                    </>
                  ) : null}
                </SelectContent>
              </Select>
            </label>
          )}
          <div className="min-w-0 space-y-1 text-right">
            <span className="block text-xs text-muted-foreground">Amount</span>
            {/* A margin-share row is worth a share of a figure this editor is
                only sometimes given. With the contribution in hand it prints the
                money; without it, the percentage — which is what the operator
                authored and can still check — rather than a confident $0.00. */}
            {marginPercentFor(line, profiles) != null ? (
              <strong className="block h-10 pt-2 text-sm tabular-nums">
                {contribution == null
                  ? `${marginPercentFor(line, profiles)}% of margin`
                  : money(marginShareLaborTotal([line], profiles, contribution))}
              </strong>
            ) : (
              <strong className="block h-10 pt-2 text-sm tabular-nums">
                {money(laborLineAmount(line, profiles))}
              </strong>
            )}
          </div>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="col-span-2 justify-self-end @md:col-span-1 @md:mt-5"
            aria-label="Remove labor line"
            disabled={lines.length === 1}
            onClick={() => onChange(lines.filter((_, i) => i !== index))}
          >
            <Trash2 size={14} />
          </Button>

          {/* WHY the amount is still $0.00 with a duration filled in.
              Duration alone cannot produce an amount, and an operator who has
              just typed "1h" and watched Amount stay at zero has no way to tell
              a broken control from a missing rate. Said on the row rather than
              under the table so it points at the field that fixes it.

              One rate answers both modes — a profile's tiers are the whole
              answer — so a zero here is always a missing rate, never a missing
              second number. */}
          {zeroAmountReason(line, profiles) ? (
            <p className="col-span-2 text-xs text-muted-foreground @md:col-span-5">
              {zeroAmountReason(line, profiles)}
            </p>
          ) : null}
        </div>
      ))}

      <div className="text-right text-sm">
        {copy.totalLabel}:{' '}
        <strong data-testid={basis === 'COST' ? 'line-labor-cost' : 'line-labor-billing'}>
          {money(total)}
        </strong>
      </div>

      {missingRateMinutes > 0 ? (
        <p className="text-xs text-warning">
          Some of this time has no rate behind it, so it is counted as free and the margin reads
          higher than it is.
        </p>
      ) : null}
    </div>
  );
}
